'use client';
import cn from 'classnames';
import type { MouseEventHandler } from 'react';

export enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
  IconText = 'iconText',
  SecondaryAlert = 'secondaryAlert',
  IconTextSecondary = 'iconTextSecondary',
  IconOnlyRound = 'iconOnlyRound',
  Warning = 'warning',
}

export enum ButtonSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  Default = 'default',
}

const getContainerClasses = (type: ButtonType, size: ButtonSize, isFullSize?: boolean) => {
  const isLarge = size === ButtonSize.Large;
  const isSmall = size === ButtonSize.Small;
  const isIconOnlyRound = type === ButtonType.IconOnlyRound;
  const isWarning = type === ButtonType.Warning;

  return cn('flex justify-center items-center cursor-pointer', {
    'min-w-44 min-h-44 md:min-w-[unset] md:min-h-[unset]': !isWarning,
    'p-0': isSmall && isIconOnlyRound,
    'p-5 md:p-1 lg:p-0': isLarge && isIconOnlyRound,
    'py-4 md:p-0': isLarge && !isIconOnlyRound && !isWarning,
    'py-1 md:p-0': !isLarge && !isSmall && !isWarning,
    'w-full': isFullSize,
  });
};

const getButtonClasses = (
  type: ButtonType,
  size: ButtonSize,
  isRectangular: boolean,
  isFullSize?: boolean,
  noBorder?: boolean,
) => {
  const isLarge = size === ButtonSize.Large;
  const isSmall = size === ButtonSize.Small;
  const isMedium = size === ButtonSize.Medium;
  const isIconTextLike = type === ButtonType.IconText || type === ButtonType.IconTextSecondary;
  const isIconOnlyRound = type === ButtonType.IconOnlyRound;
  const isSecondaryLike =
    type === ButtonType.Secondary || type === ButtonType.SecondaryAlert || type === ButtonType.IconTextSecondary;

  return cn(
    'flex items-center justify-center text-center text-sm leading-sm cursor-pointer outline-none border-0',
    'disabled:cursor-not-allowed disabled:opacity-70',
    {
      'w-full': isFullSize,
      'border-1 border-gray-400': (isSecondaryLike || isIconOnlyRound) && !noBorder,
    },

    // ---- Background + text colour per type ----
    {
      'bg-gray-800 text-white': type === ButtonType.Primary,
      'bg-red-600 text-white': type === ButtonType.Warning,
      'bg-white text-gray-800': type === ButtonType.Secondary || type === ButtonType.IconTextSecondary || isIconOnlyRound,
      'bg-white text-red-600': type === ButtonType.SecondaryAlert,
      'bg-transparent text-gray-800 font-medium': type === ButtonType.Tertiary,
    },

    // ---- Border radius (rectangular overrides the rounded pill) ----
    {
      'rounded-md': isRectangular && type !== ButtonType.IconOnlyRound,
      'rounded-[21px] md:rounded-[23px]':
        !isRectangular && !isIconTextLike && type !== ButtonType.Tertiary && type !== ButtonType.IconOnlyRound,
      'rounded-[80px]': !isRectangular && isIconTextLike,
      'rounded-full': type === ButtonType.IconOnlyRound,
    },

    // ---- Padding per type ----
    {
      'pt-10 px-16 pb-12 md:pt-12 md:px-16 md:pb-14': type === ButtonType.Primary || type === ButtonType.Warning,
      'pt-[9px] px-16 pb-[11px] md:px-16': type === ButtonType.SecondaryAlert,
      'pt-[9px] px-16 pb-[11px] md:pt-[11px] md:px-16 md:pb-[13px]': type === ButtonType.Secondary,
      'p-0': type === ButtonType.Tertiary || type === ButtonType.IconOnlyRound,
      'pt-6 px-0 pb-8 gap-8 text-base md:pt-8 md:px-0 md:pb-10 md:leading-base md:min-w-[42px] lg:pt-10 lg:px-0 lg:pb-12':
        type === ButtonType.IconText,
      'px-10 pt-5 pb-6 gap-8 text-base md:leading-base md:min-w-[42px]': type === ButtonType.IconTextSecondary,
    },

    // ---- Tertiary desktop font-size bump (min-width 1440px) ----
    { '2xl:text-base font-medium': type === ButtonType.Tertiary },

    // ---- IconOnlyRound dimensions + nested icon glyph sizing ----
    {
      '[&>i]:text-gray-800 [&>i]:p-0': isIconOnlyRound,
      'w-20 h-20 [&>i]:text-xs': isIconOnlyRound && isSmall,
      'w-[46px] h-[46px] [&>i]:text-xl': isIconOnlyRound && !isLarge && !isSmall,
      'w-[34px] h-[34px] [&>i]:text-base md:w-36 md:h-36 md:min-w-auto md:[&>i]:text-xl lg:w-40 lg:h-40':
        isIconOnlyRound && isLarge,
    },

    // ---- Size: large ----
    {
      'h-40 gap-4 font-normal': isLarge && type !== ButtonType.IconOnlyRound && type !== ButtonType.Warning,
      'h-36 pt-[7px] px-16 pb-[9px] font-medium': isLarge && type === ButtonType.Warning,
    },

    // ---- Size: medium ----
    {
      'h-36 gap-4 font-normal text-xs leading-xs md:text-sm md:h-40 md:leading-sm md:px-16 md:py-0 lg:px-16 lg:h-36 lg:text-xs lg:leading-xs':
        isMedium,
    },
  );
};

export default function Button({
  onClick,
  children,
  isFullSize,
  isRectangular = false,
  type = ButtonType.Primary,
  isDisabled,
  testId,
  size = ButtonSize.Default,
  noBorder,
}: Readonly<{
  onClick: MouseEventHandler;
  children: React.ReactNode;
  isFullSize?: boolean;
  isRectangular?: boolean;
  type?: ButtonType;
  isDisabled?: boolean;
  testId?: string;
  size?: ButtonSize;
  noBorder?: boolean;
}>) {
  return (
    <div
      className={getContainerClasses(type, size, isFullSize)}
      onClick={e => {
        if (!isDisabled) {
          onClick(e);
        }
      }}
    >
      <button
        className={getButtonClasses(type, size, isRectangular, isFullSize, noBorder)}
        data-testid={testId}
        disabled={isDisabled}
      >
        {children}
      </button>
    </div>
  );
}
